import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomReportForm } from '../components/custom-reports/CustomReportForm';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

type Brand = {
  id: string;
  name: string;
};

export default function AdminCustomReportBuilder() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<any>(null);

  useEffect(() => {
    loadBrands();
    if (isEdit && id) {
      loadReport();
    }
  }, [id, isEdit]);

  async function loadBrands() {
    try {
      const res = await fetch('/api/admin/brands', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  }

  async function loadReport() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/custom-reports/${id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const report = await res.json();
        // Parse JSON fields
        let parsedData: any = {
          campaignName: report.campaignName || '',
          brandId: report.brandId || '',
          reportDate: report.reportDate?.split('T')[0] || '',
          campaignStartDate: report.campaignStartDate?.split('T')[0] || '',
          campaignEndDate: report.campaignEndDate?.split('T')[0] || '',
          heroImages: [],
          descriptionSections: {
            executiveSummary: '',
            keyInsights: '',
            recommendations: '',
            strategicTakeaways: '',
            customerFeedback: '',
          },
        };

        // Parse heroImages
        if (report.heroImages) {
          try {
            parsedData.heroImages = typeof report.heroImages === 'string' 
              ? JSON.parse(report.heroImages) 
              : report.heroImages;
          } catch (e) {
            console.error('Failed to parse heroImages:', e);
            parsedData.heroImages = [];
          }
        }

        // Parse customReportData
        if (report.customReportData) {
          try {
            const customData = typeof report.customReportData === 'string' 
              ? JSON.parse(report.customReportData) 
              : report.customReportData;
            parsedData = { ...parsedData, ...customData };
          } catch (e) {
            console.error('Failed to parse customReportData:', e);
          }
        }

        // Parse reportData (existing fields)
        if (report.reportData) {
          try {
            const reportData = typeof report.reportData === 'string' 
              ? JSON.parse(report.reportData) 
              : report.reportData;
            parsedData = { ...parsedData, ...reportData };
          } catch (e) {
            console.error('Failed to parse reportData:', e);
          }
        }

        setInitialData(parsedData);
      }
    } catch (error) {
      console.error('Failed to load report:', error);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(formData: any) {
    setSaving(true);
    setError(null);
    try {
      const {
        campaignName,
        brandId,
        reportDate,
        campaignStartDate,
        campaignEndDate,
        heroImages,
        descriptionSections,
        ...restData
      } = formData;

      // Separate existing reportData fields from new custom fields
      const existingReportFields = [
        'daysElapsed',
        'daysRemaining',
        'campaignId',
        'totalImpressions',
        'averageImpressionsPerMachine',
        'targetImpressions',
        'doohAdPlays',
        'averagePlaysPerMachine',
        'totalScreenTimeMinutes',
        'homepageBannerImpressions',
        'categoryBannerImpressions',
        'clickThroughRate',
        'productPageViews',
        'engagementRate',
        'overallSalesPercentIncrease',
        'products',
        'preCampaignMarketShareProducts',
        'postCampaignMarketShareProducts',
        'genderMale',
        'genderFemale',
        'age1824',
        'age2534',
        'age3544',
        'age4554',
        'age55Plus',
        'peakHours',
        'peakDays',
        'purchaseTimingMorning',
        'purchaseTimingAfternoon',
        'purchaseTimingEvening',
        'totalCampaignBudget',
        'percentCampaignElapsed',
        'percentBudgetUtilized',
        'pacingStatus',
      ];

      const reportData: any = {};
      const customReportData: any = {};

      Object.keys(restData).forEach((key) => {
        if (existingReportFields.includes(key)) {
          reportData[key] = restData[key];
        } else {
          customReportData[key] = restData[key];
        }
      });

      // Add description sections to customReportData
      customReportData.descriptionSections = descriptionSections;

      const payload: any = {
        brandId: brandId || null,
        campaignName: campaignName || null,
        reportType: 'mid-campaign' as const,
        campaignStartDate: campaignStartDate ? new Date(campaignStartDate).toISOString() : null,
        campaignEndDate: campaignEndDate ? new Date(campaignEndDate).toISOString() : null,
        reportDate: reportDate ? new Date(reportDate).toISOString() : null,
        reportData: JSON.stringify(reportData),
        heroImages: heroImages || [],
        customReportData: JSON.stringify(customReportData),
      };

      let reportId = id;
      if (!isEdit) {
        // Create new report
        const createRes = await fetch('/api/admin/custom-reports', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        if (!createRes.ok) {
          const error = await createRes.json();
          throw new Error(error.error || 'Failed to create custom report');
        }

        const createdReport = await createRes.json();
        reportId = createdReport.id;
        navigate(`/admin/custom-reports/${reportId}/view`);
      } else {
        // Update existing report
        const updateRes = await fetch(`/api/admin/custom-reports/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        if (!updateRes.ok) {
          const error = await updateRes.json();
          throw new Error(error.error || 'Failed to update custom report');
        }

        navigate(`/admin/custom-reports/${id}/view`);
      }
    } catch (error) {
      console.error('Error saving report:', error);
      setError(error instanceof Error ? error.message : 'Failed to save custom report');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="container mx-auto p-6">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" onClick={() => navigate('/admin/custom-reports')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#183df7' }}>
            {isEdit ? 'Edit Custom Report' : 'Create Custom Report'}
          </h1>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <CustomReportForm
          reportId={id}
          brands={brands}
          initialData={initialData}
          onSave={handleSave}
          saving={saving}
        />
      </div>
    </div>
  );
}
